import type { LocalCommand } from './local-commands';

export type LocalCommandResultGroupPlacement = 'before-results' | 'after-results';

export type LocalCommandResultGroup = {
  id: string;
  heading: string;
  placement: LocalCommandResultGroupPlacement;
  commands: LocalCommand[];
};

export type PositionedLocalCommandResultGroup = LocalCommandResultGroup & {
  /** 0-based count of items rendered before this group, used to compute 1-based Result Position. */
  baseOffset: number;
};

type BuildLocalCommandResultPlanArgs = {
  commandGroups: LocalCommandResultGroup[];
  localSearchCount: number;
  apiResultsCount: number;
  hasApiResults: boolean;
  isLocalResultsEnabled: boolean;
};

type PositionArgs = {
  groupOffset?: number;
  itemIndex: number;
};

type LocalCommandResultPlan = {
  beforeResultGroups: PositionedLocalCommandResultGroup[];
  afterResultGroups: PositionedLocalCommandResultGroup[];
  localCommandCount: number;
  localSearchCount: number;
  apiResultsCount: number;
  totalVisibleCount: number;
  getLocalSearchPosition: (args: PositionArgs) => number;
  getApiResultPosition: (args: PositionArgs) => number;
};

function commandCount(groups: LocalCommandResultGroup[]): number {
  return groups.reduce((count, group) => count + group.commands.length, 0);
}

function withBaseOffsets(
  groups: LocalCommandResultGroup[],
  initialOffset: number,
): PositionedLocalCommandResultGroup[] {
  let nextOffset = initialOffset;

  return groups.map((group) => {
    const positionedGroup = { ...group, baseOffset: nextOffset };
    nextOffset += group.commands.length;
    return positionedGroup;
  });
}

export function buildLocalCommandResultPlan({
  commandGroups,
  localSearchCount,
  apiResultsCount,
  hasApiResults,
  isLocalResultsEnabled,
}: BuildLocalCommandResultPlanArgs): LocalCommandResultPlan {
  const visibleCommandGroups = isLocalResultsEnabled ? commandGroups.filter((group) => group.commands.length > 0) : [];
  const visibleLocalSearchCount = isLocalResultsEnabled ? localSearchCount : 0;
  const beforePlacementGroups = visibleCommandGroups.filter((group) => group.placement === 'before-results');
  const afterPlacementGroups = visibleCommandGroups.filter((group) => group.placement === 'after-results');
  const preResultGroups = hasApiResults ? beforePlacementGroups : [...beforePlacementGroups, ...afterPlacementGroups];
  const postResultGroups = hasApiResults ? afterPlacementGroups : [];
  const beforeResultCommandCount = commandCount(preResultGroups);
  const localCommandCount = commandCount(visibleCommandGroups);
  const apiResultsOffset = beforeResultCommandCount + visibleLocalSearchCount;
  const afterResultGroupsOffset = beforeResultCommandCount + visibleLocalSearchCount + apiResultsCount;
  const totalVisibleCount = localCommandCount + visibleLocalSearchCount + apiResultsCount;

  return {
    beforeResultGroups: withBaseOffsets(preResultGroups, 0),
    afterResultGroups: withBaseOffsets(postResultGroups, afterResultGroupsOffset),
    localCommandCount,
    localSearchCount: visibleLocalSearchCount,
    apiResultsCount,
    totalVisibleCount,
    getLocalSearchPosition: ({ groupOffset = 0, itemIndex }) => 1 + beforeResultCommandCount + groupOffset + itemIndex,
    getApiResultPosition: ({ groupOffset = 0, itemIndex }) => 1 + apiResultsOffset + groupOffset + itemIndex,
  };
}
